import React, { FunctionComponent, useRef, useState } from 'react';
import styles from './SchoolPhotos.module.scss';
import Text from 'common/components/Text/Text';
import DragAndDrop from 'common/components/AppNavbar/DragAndDrop/DragAndDrop';
import { useFormik } from 'formik';
import ConfirmationBox from 'common/components/ConfirmationBox/ConfirmationBox';

import {
  useGetBackofficeSchoolQuery,
  useAddSchoolAttachmentMutation,
  useRemoveSchoolAttachmentMutation,
} from 'services/exploreApi';
import { useParams } from 'react-router-dom';

interface FileProps {
  logo?: File;
  cover?: File;
  photos?: File[];
}
interface InputProps {
  handleFile: (event: React.ChangeEvent<HTMLInputElement>) => void;
  name: string;
}
const UploadLogic = ({ handleFile, name }: InputProps) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const showOpenFileDialog = () => {
    fileRef?.current?.click();
  };
  return (
    <div className={styles.options}>
      <button
        className={`${styles.button} ${styles.add}`}
        onClick={showOpenFileDialog}
        type="button"
      >
        <i className="bi bi-plus" />
      </button>
      <input
        type="file"
        style={{ display: 'none' }}
        accept=".pdf, .jpg, .png"
        name={name}
        onChange={handleFile}
        ref={fileRef}
      />
    </div>
  );
};
const SchoolPhotos: FunctionComponent = () => {
  const { id } = useParams<{ id: string }>();
  const { data: school, isLoading } = useGetBackofficeSchoolQuery(parseInt(id || ''));
  const [addAttachments, { data: newAttachment, isLoading: isLoadingAttachment }] =
    useAddSchoolAttachmentMutation();
  const [removeAttachment] = useRemoveSchoolAttachmentMutation();
  const [photos, setPhotos] = useState<File[]>([]);
  const [logoPreview, setLogoPreview] = useState<File | undefined>();
  const [coverPreview, setCoverPreview] = useState<File | undefined>();
  const handlePhotoDelete = async (photo_index: number) => {
    await removeAttachment({ id: school?.data.id, data: { photo_index: photo_index } });
    window.location.reload();
  };
  const formik = useFormik<FileProps>({
    initialValues: {
      logo: undefined,
      cover: undefined,
    },
    onSubmit: async ({ logo, cover }: FileProps) => {
      const formData = new FormData();
      if (logo) {
        formData.append('school[logo]', logo);
      }
      if (cover) {
        formData.append('school[cover]', cover);
      }
      if (photos) {
        photos.map((photo) => formData.append(`school[photos][]`, photo));
      }
      await addAttachments({ data: formData, school_id: school?.data.id }).then(() => {
        setLogoPreview(undefined);
        setCoverPreview(undefined);
        setPhotos([]);
        window.location.reload();
      });
    },
  });
  const handleDelete = async (e: React.MouseEvent<HTMLElement>, kind: string) => {
    await removeAttachment({ id: school?.data.id, data: { kind: kind } }).then(() => {
      window.location.reload();
    });
  };

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    formik.setFieldValue(e.target.name, file);
    e.target.name === 'cover' ? setCoverPreview(file) : setLogoPreview(file);
  };

  const handleDragAndDropFiles = (files: File[]) => {
    setPhotos((prevState: File[]) => [...prevState, ...files]);
  };

  return (
    <form className={styles.container} onSubmit={formik.handleSubmit} id="schoolForm">
      <div className={styles.firstWrap}>
        <Text size={40} weight={700}>
          {school?.data.name}
        </Text>
        <button
          className={`${styles.exitButton} btn`}
          type="submit"
          form="schoolForm"
          disabled={isLoadingAttachment}
        >
          Save
        </button>
      </div>
      <div className={styles.options}>
        <Text size={24} weight={400}>
          Logo
        </Text>
        <div className={styles.buttons}>
          <UploadLogic handleFile={handleFile} name="logo" />
          <button
            className={`${styles.button} ${styles.trash}`}
            onClick={(e) => handleDelete(e, 'logo')}
          >
            <i className="bi bi-trash" />
          </button>
        </div>
      </div>
      <img
        src={logoPreview ? URL.createObjectURL(logoPreview) : school?.data.logo}
        className={styles.logo}
      />
      <div className={styles.options}>
        <Text size={24} weight={400}>
          Cover
        </Text>
        <div className={styles.buttons}>
          <UploadLogic handleFile={handleFile} name="cover" />
          <button
            className={`${styles.button} ${styles.trash}`}
            onClick={(e) => handleDelete(e, 'cover')}
          >
            <i className="bi bi-trash" />
          </button>
        </div>
      </div>
      <img
        src={coverPreview ? URL.createObjectURL(coverPreview) : school?.data.cover}
        className={styles.cover}
      />
      <Text size={24} weight={400}>
        Photo
      </Text>
      <DragAndDrop callback={handleDragAndDropFiles}>
        {photos.length === 0 ? (
          <Text>Drop your files here</Text>
        ) : (
          <div>
            {photos.map((file: File, i: number) => (
              <div key={i}>{file.name}</div>
            ))}
          </div>
        )}
      </DragAndDrop>
      <div className={styles.flex}>
        {school?.data.photos?.map((photo: string, index: number) => (
          <div key={photo} className={styles.imgContainer}>
            <img src={photo} className={styles.img} />
            <button
              className={styles.deleteImgButton}
              onClick={() => handlePhotoDelete(index)}
              type="button"
            >
              <i className="bi bi-trash" />
            </button>
          </div>
        ))}
      </div>
      {/* <ConfirmationBox /> */}
    </form>
  );
};

export default SchoolPhotos;
